import { useState, useEffect, useCallback } from 'react'
import { adminService } from '../../services/adminService'
import {
  FileText,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Calendar,
} from 'lucide-react'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface AuditEntry {
  id: string
  actorId: string
  actorName: string
  action: string
  targetEntityType: string
  targetEntityId: string
  previousValue: unknown
  newValue: unknown
  ipAddress: string
  timestamp: string
}

interface PaginatedResponse {
  items: AuditEntry[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

interface Filters {
  actor: string
  actionType: string
  targetEntityType: string
  dateFrom: string
  dateTo: string
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const ACTION_TYPES = [
  { value: '', label: 'All Actions' },
  { value: 'user.created', label: 'User Created' },
  { value: 'user.updated', label: 'User Updated' },
  { value: 'user.disabled', label: 'User Disabled' },
  { value: 'user.enabled', label: 'User Enabled' },
  { value: 'user.deleted', label: 'User Deleted' },
  { value: 'user.password_reset', label: 'Password Reset' },
  { value: 'user.email_verified', label: 'Email Verified' },
  { value: 'business.updated', label: 'Business Updated' },
  { value: 'business.recalculated', label: 'Score Recalculated' },
  { value: 'impersonation.start', label: 'Impersonation Start' },
  { value: 'impersonation.end', label: 'Impersonation End' },
  { value: 'system.setting_updated', label: 'Setting Updated' },
  { value: 'system.feature_flag_toggled', label: 'Feature Flag Toggled' },
  { value: 'permission.granted', label: 'Permission Granted' },
  { value: 'permission.denied', label: 'Permission Denied' },
  { value: 'subscription.assigned', label: 'Subscription Assigned' },
  { value: 'subscription.changed', label: 'Subscription Changed' },
  { value: 'api_key.created', label: 'API Key Created' },
  { value: 'api_key.revoked', label: 'API Key Revoked' },
]

const TARGET_ENTITY_TYPES = [
  { value: '', label: 'All Entities' },
  { value: 'user', label: 'User' },
  { value: 'business', label: 'Business' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'feature_flag', label: 'Feature Flag' },
  { value: 'permission', label: 'Permission' },
  { value: 'system_setting', label: 'System Setting' },
  { value: 'cron_job', label: 'Cron Job' },
  { value: 'api_key', label: 'API Key' },
  { value: 'lender', label: 'Lender' },
]

const PAGE_SIZES = [10, 25, 50, 100]

// ─── Component ─────────────────────────────────────────────────────────────────

export function AdminAuditLog() {
  // Data state
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Filters
  const [filters, setFilters] = useState<Filters>({
    actor: '',
    actionType: '',
    targetEntityType: '',
    dateFrom: '',
    dateTo: '',
  })

  // Expanded rows for details
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  // Export state
  const [exporting, setExporting] = useState(false)

  // ─── Data Fetching ─────────────────────────────────────────────────────────

  const fetchAuditLog = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, any> = {
        page,
        pageSize,
      }
      if (filters.actor) params.actor = filters.actor
      if (filters.actionType) params.actionType = filters.actionType
      if (filters.targetEntityType) params.targetEntityType = filters.targetEntityType
      if (filters.dateFrom) params.dateFrom = filters.dateFrom
      if (filters.dateTo) params.dateTo = filters.dateTo

      const response = await adminService.getAuditLog(params)
      const data = response.data as PaginatedResponse
      setEntries(data.items || [])
      setTotal(data.total || 0)
      setTotalPages(data.totalPages || 0)
    } catch (err: any) {
      setError(err.message || 'Failed to load audit log')
      setEntries([])
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, filters])

  useEffect(() => {
    fetchAuditLog()
  }, [fetchAuditLog])

  // ─── Filter Handlers ───────────────────────────────────────────────────────

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const clearFilters = () => {
    setFilters({
      actor: '',
      actionType: '',
      targetEntityType: '',
      dateFrom: '',
      dateTo: '',
    })
    setPage(1)
  }

  const hasActiveFilters =
    filters.actor ||
    filters.actionType ||
    filters.targetEntityType ||
    filters.dateFrom ||
    filters.dateTo

  // ─── Export Handler ────────────────────────────────────────────────────────

  const handleExport = async () => {
    setExporting(true)
    try {
      const params: Record<string, any> = {}
      if (filters.actor) params.actor = filters.actor
      if (filters.actionType) params.actionType = filters.actionType
      if (filters.targetEntityType) params.targetEntityType = filters.targetEntityType
      if (filters.dateFrom) params.dateFrom = filters.dateFrom
      if (filters.dateTo) params.dateTo = filters.dateTo

      const response = await adminService.exportAuditLog(params)
      const data = response.data

      // If the response contains a download URL, open it
      if (data?.downloadUrl) {
        window.open(data.downloadUrl, '_blank')
      } else if (data?.csv || typeof data === 'string') {
        // If the response is raw CSV data, trigger a download
        const csvContent = data?.csv || data
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `audit-log-export-${new Date().toISOString().split('T')[0]}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to export audit log')
    } finally {
      setExporting(false)
    }
  }

  // ─── Row Expand/Collapse ───────────────────────────────────────────────────

  const toggleRowExpansion = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // ─── Pagination Helpers ────────────────────────────────────────────────────

  const getPageNumbers = (): (number | '...')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const pages: (number | '...')[] = [1]
    if (page > 3) pages.push('...')
    const start = Math.max(2, page - 1)
    const end = Math.min(totalPages - 1, page + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (page < totalPages - 2) pages.push('...')
    pages.push(totalPages)
    return pages
  }

  // ─── Render Helpers ────────────────────────────────────────────────────────

  const formatTimestamp = (dateStr: string) => {
    if (!dateStr) return '—'
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    } catch {
      return '—'
    }
  }

  const formatActionBadge = (action: string) => {
    const colorMap: Record<string, string> = {
      created: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      updated: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      deleted: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      disabled: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      enabled: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      granted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
      denied: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      revoked: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      start: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      end: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    }

    // Extract the last part of the action for color matching
    const parts = action.split('.')
    const suffix = parts[parts.length - 1]
    const colorClass =
      colorMap[suffix] ||
      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'

    return colorClass
  }

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—'
    if (typeof value === 'string') return value
    try {
      return JSON.stringify(value, null, 2)
    } catch {
      return String(value)
    }
  }

  const hasDetails = (entry: AuditEntry): boolean => {
    return (
      (entry.previousValue !== null && entry.previousValue !== undefined) ||
      (entry.newValue !== null && entry.newValue !== undefined)
    )
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <FileText className="h-7 w-7 text-blue-600 dark:text-blue-400" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Audit Log
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              View and export administrative action history
            </p>
          </div>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Actor Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by actor..."
              value={filters.actor}
              onChange={(e) => handleFilterChange('actor', e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Action Type Filter */}
          <select
            value={filters.actionType}
            onChange={(e) => handleFilterChange('actionType', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {ACTION_TYPES.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>

          {/* Target Entity Type Filter */}
          <select
            value={filters.targetEntityType}
            onChange={(e) => handleFilterChange('targetEntityType', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {TARGET_ENTITY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>

        {/* Date Range */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-3">
          <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            <Calendar className="h-4 w-4" />
            <span className="whitespace-nowrap">Date range:</span>
          </div>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <span className="text-sm text-gray-400">to</span>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={fetchAuditLog}
            className="ml-auto inline-flex items-center gap-1 text-sm text-red-700 dark:text-red-300 hover:underline"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </button>
        </div>
      )}

      {/* Audit Log Table */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
            <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
              Loading audit log...
            </span>
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <FileText className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {hasActiveFilters
                ? 'No audit entries match the current filters'
                : 'No audit log entries found'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300 w-8" />
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Timestamp
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Actor
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Action
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Target
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {entries.map((entry) => (
                    <>
                      <tr
                        key={entry.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                      >
                        {/* Expand/Collapse */}
                        <td className="px-4 py-3">
                          {hasDetails(entry) && (
                            <button
                              onClick={() => toggleRowExpansion(entry.id)}
                              className="p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                              aria-label={
                                expandedRows.has(entry.id)
                                  ? 'Collapse details'
                                  : 'Expand details'
                              }
                            >
                              {expandedRows.has(entry.id) ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </td>

                        {/* Timestamp */}
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          {formatTimestamp(entry.timestamp)}
                        </td>

                        {/* Actor */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {entry.actorName || '—'}
                          </span>
                        </td>

                        {/* Action */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${formatActionBadge(entry.action)}`}
                          >
                            {entry.action}
                          </span>
                        </td>

                        {/* Target */}
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          {entry.targetEntityType && entry.targetEntityId ? (
                            <span>
                              <span className="text-gray-500 dark:text-gray-400">
                                {entry.targetEntityType}
                              </span>
                              {' / '}
                              <span className="font-mono text-xs">
                                {entry.targetEntityId.length > 12
                                  ? `${entry.targetEntityId.substring(0, 12)}...`
                                  : entry.targetEntityId}
                              </span>
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>

                        {/* Details summary */}
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                          {hasDetails(entry) ? (
                            <button
                              onClick={() => toggleRowExpansion(entry.id)}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {expandedRows.has(entry.id)
                                ? 'Hide details'
                                : 'View details'}
                            </button>
                          ) : (
                            <span className="text-xs">—</span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded Details Row */}
                      {expandedRows.has(entry.id) && hasDetails(entry) && (
                        <tr
                          key={`${entry.id}-details`}
                          className="bg-gray-50 dark:bg-gray-900/30"
                        >
                          <td colSpan={6} className="px-4 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
                              {/* Previous Value */}
                              <div>
                                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                                  Previous Value
                                </h4>
                                <pre className="text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 overflow-x-auto max-h-40">
                                  {formatValue(entry.previousValue)}
                                </pre>
                              </div>
                              {/* New Value */}
                              <div>
                                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                                  New Value
                                </h4>
                                <pre className="text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 overflow-x-auto max-h-40">
                                  {formatValue(entry.newValue)}
                                </pre>
                              </div>
                            </div>
                            {/* IP Address */}
                            {entry.ipAddress && (
                              <div className="mt-3">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  IP Address:{' '}
                                </span>
                                <span className="text-xs font-mono text-gray-700 dark:text-gray-300">
                                  {entry.ipAddress}
                                </span>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span>
                  Showing {(page - 1) * pageSize + 1}–
                  {Math.min(page * pageSize, total)} of {total}
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value))
                    setPage(1)
                  }}
                  className="ml-2 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {getPageNumbers().map((p, i) =>
                  p === '...' ? (
                    <span
                      key={`ellipsis-${i}`}
                      className="px-2 text-gray-400 dark:text-gray-500"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        p === page
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
